import React, { useEffect, useState, useRef } from 'react';
import { adminGetDeposits, adminManualPayment, adminGetClients } from '../../api';

const statusBadge = (st) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
    background: ['paid','paid_over'].includes(st) ? '#d1fae5' : st === 'waiting' ? '#fef3c7' : '#fee2e2',
    color:      ['paid','paid_over'].includes(st) ? '#065f46' : st === 'waiting' ? '#92400e' : '#991b1b',
});

const STATUSES = ['all', 'waiting', 'paid', 'paid_over', 'expired', 'cancel'];

const s = {
    h1:      { fontSize: 22, fontWeight: 700, marginBottom: 20 },
    statsRow:{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 },
    stat:    { background: '#fff', borderRadius: 10, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,.06)' },
    statLbl: { fontSize: 12, color: '#6b7280', marginBottom: 4 },
    statVal: { fontSize: 24, fontWeight: 700, color: '#1a1d23' },
    filter:  { display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' },
    sel:     { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: '#fff' },
    inp:     { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, width: 160 },
    panel:   { background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,.06)', overflow: 'hidden' },
    table:   { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th:      { textAlign: 'left', padding: '12px 16px', background: '#f9fafb', borderBottom: '2px solid #f3f4f6', color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: .5 },
    td:      { padding: '11px 16px', borderBottom: '1px solid #f9fafb', verticalAlign: 'middle' },
    empty:   { padding: '32px 20px', textAlign: 'center', color: '#9ca3af' },
    link:    { color: '#6366f1', textDecoration: 'none', fontSize: 12 },
    clientBadge: { display: 'inline-block', padding: '2px 8px', background: '#ede9fe', borderRadius: 8, fontSize: 12, color: '#5b21b6', fontWeight: 600 },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 },
    dialog:  { background: '#fff', borderRadius: 14, padding: '28px 32px', maxWidth: 440, width: '90%', boxShadow: '0 16px 48px rgba(0,0,0,0.2)' },
    dTitle:  { fontSize: 18, fontWeight: 700, color: '#1a1d23', marginBottom: 16 },
    dLabel:  { fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 },
    dInput:  { width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, marginBottom: 14, boxSizing: 'border-box' },
    dBtnRow: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 },
    dBtnCancel: { padding: '10px 20px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' },
    dBtnConfirm: { padding: '10px 20px', border: 'none', borderRadius: 8, background: '#059669', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
    addBtn:  { padding: '8px 16px', background: '#059669', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
};

export default function AdminPayments() {
    const [data, setData]         = useState({ deposits: [], stats: {} });
    const [status, setStatus]     = useState('all');
    const [clientFilter, setClientFilter] = useState('');
    const [loading, setLoading]   = useState(true);
    const [showManual, setShowManual] = useState(false);
    const [manualForm, setManualForm] = useState({ client_id: '', amount: '', note: '' });
    const [saving, setSaving] = useState(false);
    const [allClients, setAllClients] = useState([]);
    const [clientSearch, setClientSearch] = useState('');
    const [showClientDropdown, setShowClientDropdown] = useState(false);
    const dropdownRef = useRef(null);

    const load = () => {
        setLoading(true);
        const params = { limit: 200 };
        if (status !== 'all') params.status = status;
        if (clientFilter) params.client_id = clientFilter;
        adminGetDeposits(params)
            .then(r => setData(r.data || { deposits: [], stats: {} }))
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, [status, clientFilter]); // eslint-disable-line

    useEffect(() => {
        adminGetClients().then(r => setAllClients(r.data || [])).catch(() => {});
    }, []);

    // Close dropdown on click outside
    useEffect(() => {
        const handler = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowClientDropdown(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleManualSubmit = async () => {
        const amount = parseFloat(manualForm.amount);
        if (!manualForm.client_id || !amount || amount <= 0) {
            alert('Enter a valid client ID and positive amount');
            return;
        }
        setSaving(true);
        try {
            await adminManualPayment({
                client_id: parseInt(manualForm.client_id),
                amount,
                note: manualForm.note,
            });
            setShowManual(false);
            setManualForm({ client_id: '', amount: '', note: '' });
            setClientSearch('');
            load();
        } catch (err) {
            alert('Failed: ' + (err.response?.data?.error || err.message));
        } finally {
            setSaving(false);
        }
    };

    const stats = data.stats || {};
    const deposits = data.deposits || [];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h1 style={{ ...s.h1, marginBottom: 0 }}>Payments & Deposits</h1>
                <button style={s.addBtn} onClick={() => setShowManual(true)}>
                    + Add Manual Payment
                </button>
            </div>

            {/* Stats */}
            <div className="og-grid-4" style={s.statsRow}>
                {[
                    { lbl: 'Total Collected',  val: `€${parseFloat(stats.total_paid || 0).toFixed(2)}` },
                    { lbl: 'Paid Deposits',    val: stats.count_paid || 0 },
                    { lbl: 'Pending',          val: `€${parseFloat(stats.total_pending || 0).toFixed(2)}` },
                    { lbl: 'Pending Count',    val: stats.count_pending || 0 },
                ].map(st => (
                    <div key={st.lbl} style={s.stat}>
                        <div style={s.statLbl}>{st.lbl}</div>
                        <div style={s.statVal}>{st.val}</div>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div className="og-filters" style={s.filter}>
                <span style={{ fontSize: 13, color: '#6b7280' }}>Status:</span>
                <select style={s.sel} value={status} onChange={e => setStatus(e.target.value)}>
                    {STATUSES.map(st => <option key={st} value={st}>{st.charAt(0).toUpperCase() + st.slice(1)}</option>)}
                </select>
                <span style={{ fontSize: 13, color: '#6b7280' }}>Client ID:</span>
                <input
                    style={s.inp}
                    type="number"
                    placeholder="Filter by client ID..."
                    value={clientFilter}
                    onChange={e => setClientFilter(e.target.value)}
                />
                {clientFilter && (
                    <button onClick={() => setClientFilter('')}
                        style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, background: '#f9fafb', cursor: 'pointer', fontSize: 12 }}>
                        Clear
                    </button>
                )}
            </div>

            {/* Table */}
            <div style={s.panel}>
                {loading ? (
                    <div style={s.empty}>Loading...</div>
                ) : deposits.length === 0 ? (
                    <div style={s.empty}>No deposits found.</div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={s.table}>
                            <thead>
                                <tr>
                                    {['ID', 'Client', 'Amount', 'Bonus', 'Method', 'Status', 'Date', 'Paid At', 'Action'].map(h => (
                                        <th key={h} style={s.th}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {deposits.map(d => (
                                    <tr key={d.id}>
                                        <td style={s.td}><span style={{ color: '#6b7280', fontSize: 12 }}>#{d.id}</span></td>
                                        <td style={s.td}>
                                            <span style={s.clientBadge}>{d.username}</span>
                                            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{d.email}</div>
                                        </td>
                                        <td style={{ ...s.td, fontWeight: 700, color: '#065f46' }}>
                                            €{parseFloat(d.amount).toFixed(2)}
                                        </td>
                                        <td style={s.td}>
                                            {d.bonus_amount && parseFloat(d.bonus_amount) > 0
                                                ? <span style={{ color: '#1d4ed8', fontWeight: 700, fontSize: 12 }}>+€{parseFloat(d.bonus_amount).toFixed(2)}</span>
                                                : <span style={{ color: '#d1d5db' }}>—</span>}
                                        </td>
                                        <td style={s.td}>
                                            {d.to_currency === 'MANUAL'
                                                ? <span style={{ display: 'inline-block', padding: '2px 8px', background: '#eff6ff', borderRadius: 8, color: '#1d4ed8', fontSize: 11, fontWeight: 600 }}>Manual</span>
                                                : d.to_currency}
                                        </td>
                                        <td style={s.td}><span style={statusBadge(d.status)}>{d.status}</span></td>
                                        <td style={s.td}>{new Date(d.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                                        <td style={s.td}>
                                            {d.paid_at
                                                ? new Date(d.paid_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
                                                : <span style={{ color: '#d1d5db' }}>—</span>}
                                        </td>
                                        <td style={s.td}>
                                            {d.status === 'waiting' && d.payment_url && (
                                                <a href={d.payment_url} target="_blank" rel="noreferrer" style={s.link}>
                                                    View Invoice →
                                                </a>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Manual Payment Dialog */}
            {showManual && (
                <div style={s.overlay} onClick={() => setShowManual(false)}>
                    <div style={s.dialog} onClick={e => e.stopPropagation()}>
                        <div style={s.dTitle}>Add Manual Payment</div>
                        <div ref={dropdownRef} style={{ position: 'relative' }}>
                            <div style={s.dLabel}>Client</div>
                            <input
                                style={s.dInput}
                                type="text"
                                placeholder="Type username..."
                                value={clientSearch}
                                onChange={e => {
                                    setClientSearch(e.target.value);
                                    setShowClientDropdown(true);
                                    if (!e.target.value) setManualForm(f => ({ ...f, client_id: '' }));
                                }}
                                onFocus={() => setShowClientDropdown(true)}
                            />
                            {showClientDropdown && clientSearch.length > 0 && (() => {
                                const filtered = allClients.filter(c =>
                                    c.username?.toLowerCase().includes(clientSearch.toLowerCase()) ||
                                    c.email?.toLowerCase().includes(clientSearch.toLowerCase())
                                ).slice(0, 8);
                                if (!filtered.length) return null;
                                return (
                                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,.15)', zIndex: 10, maxHeight: 200, overflowY: 'auto' }}>
                                        {filtered.map(c => (
                                            <div key={c.id}
                                                onClick={() => {
                                                    setManualForm(f => ({ ...f, client_id: String(c.id) }));
                                                    setClientSearch(c.username);
                                                    setShowClientDropdown(false);
                                                }}
                                                style={{ padding: '8px 14px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}
                                                onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'}
                                                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                                            >
                                                <span style={{ fontWeight: 600, color: '#1a1d23' }}>{c.username}</span>
                                                <span style={{ color: '#9ca3af', marginLeft: 8, fontSize: 11 }}>#{c.id} · {c.email}</span>
                                            </div>
                                        ))}
                                    </div>
                                );
                            })()}
                        </div>
                        <div>
                            <div style={s.dLabel}>Amount (EUR)</div>
                            <input
                                style={s.dInput}
                                type="number"
                                step="0.01"
                                min="0.01"
                                placeholder="50.00"
                                value={manualForm.amount}
                                onChange={e => setManualForm(f => ({ ...f, amount: e.target.value }))}
                            />
                        </div>
                        <div>
                            <div style={s.dLabel}>Note (optional)</div>
                            <input
                                style={s.dInput}
                                type="text"
                                placeholder="e.g. Bank transfer, PayPal, etc."
                                value={manualForm.note}
                                onChange={e => setManualForm(f => ({ ...f, note: e.target.value }))}
                            />
                        </div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: -6, marginBottom: 8 }}>
                            The amount will be added to the client's balance immediately and will appear in their payment history.
                        </div>
                        <div style={s.dBtnRow}>
                            <button style={s.dBtnCancel} onClick={() => setShowManual(false)}>Cancel</button>
                            <button
                                style={{ ...s.dBtnConfirm, opacity: saving ? 0.6 : 1 }}
                                onClick={handleManualSubmit}
                                disabled={saving}
                            >
                                {saving ? 'Adding...' : 'Add Payment'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
